import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Loader2, Bookmark, CheckCircle2, XCircle, Trophy } from "lucide-react";
import { readingService } from "@/services/readingService";
import type { ReadingArticle, ReadingProgress } from "@/services/readingService";
import { wordBankService } from "@/services/wordBankService";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

export default function ReadingArticleView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [article, setArticle] = useState<ReadingArticle | null>(null);
  const [progress, setProgress] = useState<ReadingProgress | null>(null);
  const [loading, setLoading] = useState(true);

  // Quiz State
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Selection state
  const [selectedWord, setSelectedWord] = useState<string>("");
  const [selectionRect, setSelectionRect] = useState<{ top: number; left: number } | null>(null);
  const [savingWord, setSavingWord] = useState(false);
  const [wordSaved, setWordSaved] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!slug) return;
      try {
        const data = await readingService.getArticleBySlug(slug);
        if (data) {
          setArticle(data);
          // Check progress
          const allProgress = await readingService.fetchUserProgress();
          const currentProgress = allProgress.find(p => p.article_id === data.id);
          
          if (currentProgress) {
            setProgress(currentProgress);
            if (currentProgress.answers) {
              setAnswers(currentProgress.answers);
            }
            if (currentProgress.status === "completed") {
              setShowResults(true);
              setScore(currentProgress.score || 0);
            }
          }
        }
      } catch (err) {
        console.error("Error loading article:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  // Handle global selection changes to dismiss the button properly
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.toString().trim() === "") {
        setSelectionRect(null);
        setSelectedWord("");
      }
    };
    
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  // Handle Text Selection for Word Bank
  const handleSelection = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setSelectedWord("");
        setSelectionRect(null);
        setWordSaved(false);
        return;
      }
      
      const text = selection.toString().trim();
      // Allow reasonably long expressions but avoid selecting huge blocks
      if (text.length > 0 && text.length < 400 && !text.includes("\n")) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        // Match the grammar lesson positioning exactly using viewport coords
        setSelectionRect({
          top: Math.max(10, rect.top - 45),
          left: rect.left + (rect.width / 2)
        });
        setSelectedWord(text);
        setWordSaved(false);
      } else {
        setSelectedWord("");
        setSelectionRect(null);
        setWordSaved(false);
      }
    }, 10);
  };

  const handleSaveToWordBank = async () => {
    if (!selectedWord) return;
    setSavingWord(true);
    try {
      const success = await wordBankService.createWord({
        term: selectedWord,
        meaning: "Salvo via Coin Lecture",
        context: `"${selectedWord}" - Contexto: ${article?.title || ""}`,
        category: "Expressões",
        difficulty: "Intermediário"
      } as any);
      
      if (success) {
        setWordSaved(true);
        setTimeout(() => {
          setSelectedWord("");
          setSelectionRect(null);
          setWordSaved(false);
          window.getSelection()?.removeAllRanges();
        }, 2000);
      }
    } catch (err: any) {
      console.error("Failed to save word", err);
      if (err.message && (err.message.includes("já está cadastrada") || err.message.includes("duplicate"))) {
        alert("Esta frase/palavra já está cadastrada no seu banco!");
      } else {
        alert("Erro ao salvar no banco de palavras.");
      }
      setSelectedWord("");
      setSelectionRect(null);
      setWordSaved(false);
      window.getSelection()?.removeAllRanges();
    } finally {
      setSavingWord(false);
    }
  };

  const handleQuizSubmit = async () => {
    if (!article || !article.quiz) return;
    
    // Calculate Score
    let correctCount = 0;
    article.quiz.forEach((q, index) => {
      if (answers[index] === q.correct_answer_index) {
        correctCount++;
      }
    });

    const finalScore = Math.round((correctCount / article.quiz.length) * 100);
    setScore(finalScore);
    setShowResults(true);

    // Save Progress
    await readingService.updateReadingProgress(
      article.id,
      "completed",
      finalScore,
      answers
    );
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number) => {
    if (showResults) return; // Prevent changing after submit
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-32">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
        <h2 className="text-xl font-bold text-slate-700">Carregando conteúdo...</h2>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto py-20 text-center">
        <h2 className="text-2xl font-bold text-slate-800">Artigo não encontrado</h2>
        <Button onClick={() => navigate("/reading")} className="mt-4">Voltar ao catálogo</Button>
      </div>
    );
  }

  const allAnswered = article.quiz ? Object.keys(answers).length === article.quiz.length : true;

  return (
    <div 
      className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500 pb-32 relative"
      onMouseUp={handleSelection}
      onTouchEnd={handleSelection}
    >
      
      {/* Back button */}
      <div className="pt-2">
        <button 
          onClick={() => navigate("/reading")}
          className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao Catálogo
        </button>
      </div>

      {/* Article Header */}
      <div className="space-y-4 border-b border-slate-200 pb-8">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold uppercase tracking-wider">
            {article.category}
          </span>
          <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold">
            Nível {article.level}
          </span>
          <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold">
            {article.estimated_time}
          </span>
        </div>
        <h1 className="text-4xl font-black text-slate-900 leading-tight">
          {article.title}
        </h1>
      </div>

      {/* Markdown Content */}
      <div className="prose prose-slate prose-lg max-w-none text-slate-800 font-medium leading-relaxed prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-indigo-600 text-justify">
        <ReactMarkdown>{article.content}</ReactMarkdown>
      </div>

      {/* Floating Word Bank Button */}
      {selectedWord && selectionRect && createPortal(
        <div 
          className="fixed z-[9999] animate-in fade-in zoom-in-95 duration-200"
          style={{ 
            top: `${selectionRect.top}px`, 
            left: `${selectionRect.left}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <Button
            size="sm"
            onClick={handleSaveToWordBank}
            disabled={savingWord || wordSaved}
            className="rounded-full shadow-lg gap-2 bg-slate-900 hover:bg-slate-800 text-white"
          >
            {wordSaved ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Salvo!
              </>
            ) : savingWord ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Salvando
              </>
            ) : (
              <>
                <Bookmark className="w-4 h-4" /> Salvar Palavra
              </>
            )}
          </Button>
        </div>,
        document.body
      )}

      {/* Comprehension Quiz */}
      {article.quiz && article.quiz.length > 0 && (
        <div className="pt-12 border-t border-slate-200 space-y-8 select-none">
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2">
              Compréhension Écrite
            </h2>
            <p className="text-slate-500 font-medium">Responda às questões abaixo para testar sua compreensão do texto.</p>
          </div>

          <div className="space-y-6">
            {article.quiz.map((q, qIndex) => {
              const isCorrect = answers[qIndex] === q.correct_answer_index;
              const hasAnswered = answers[qIndex] !== undefined;

              return (
                <Card key={qIndex} className={`border-2 ${showResults ? (isCorrect ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30') : 'border-slate-100'}`}>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-bold text-lg text-slate-800">{qIndex + 1}. {q.question}</h3>
                    
                    <div className="space-y-3">
                      {q.options.map((opt, optIndex) => {
                        let optStyle = "border-slate-200 bg-white";
                        let isSelected = answers[qIndex] === optIndex;
                        
                        if (showResults) {
                          if (optIndex === q.correct_answer_index) {
                            optStyle = "border-emerald-500 bg-emerald-50";
                          } else if (isSelected) {
                            optStyle = "border-rose-500 bg-rose-50";
                          } else {
                            optStyle = "border-slate-200 bg-slate-50 opacity-60";
                          }
                        } else if (isSelected) {
                          optStyle = "border-indigo-600 bg-indigo-50/50";
                        }

                        return (
                          <label key={optIndex} className={`flex items-center space-x-3 border p-3 rounded-xl transition-all cursor-pointer ${optStyle} ${!showResults && 'hover:bg-slate-50'}`}>
                            <input 
                              type="radio"
                              name={`question-${qIndex}`}
                              value={optIndex.toString()} 
                              checked={isSelected}
                              onChange={() => handleOptionChange(qIndex, optIndex)}
                              disabled={showResults}
                              className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600"
                            />
                            <span 
                              className={`flex-1 font-semibold ${showResults && optIndex === q.correct_answer_index ? "text-emerald-700" : "text-slate-700"}`}
                            >
                              {opt}
                            </span>
                            {showResults && optIndex === q.correct_answer_index && (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            )}
                            {showResults && isSelected && optIndex !== q.correct_answer_index && (
                              <XCircle className="w-5 h-5 text-rose-500" />
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {showResults && (
                      <div className={`mt-4 p-4 rounded-xl ${isCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                        <p className="font-bold mb-1">{isCorrect ? 'Acertou!' : 'Errou.'}</p>
                        <p className="text-sm font-medium">{q.explanation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {!showResults && (
            <div className="flex justify-end pt-4">
              <Button 
                size="lg" 
                onClick={handleQuizSubmit} 
                disabled={!allAnswered}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
              >
                Verificar Respostas
              </Button>
            </div>
          )}

          {showResults && (
            <div className="mt-8 p-6 bg-slate-900 rounded-3xl flex flex-col items-center text-center space-y-3">
              <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mb-2">
                <Trophy className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-black text-white">Leitura Concluída!</h3>
              <p className="text-slate-300 font-medium">Você acertou {score}% das questões de interpretação.</p>
              <Button 
                onClick={() => navigate("/reading")}
                className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Voltar ao Catálogo
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
